import * as React from "react";
import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  Lock,
  MoreHorizontal,
  Pencil,
  Plus,
  RotateCcw,
  Trash2,
  Wallet,
} from "lucide-react";
import {
  Badge,
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  StatusPill,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
  toast,
} from "@eduviaos/ui-kit";
import { ApiError } from "@eduviaos/shared";
import { PayslipEditorSheet } from "@/components/payroll/PayslipEditorSheet";
import { useTerms } from "@/hooks/useTerms";
import {
  formatPaise,
  payPeriodLabel,
  useDeletePayRun,
  useDeletePayslip,
  useFinalizePayRun,
  usePayRun,
  useReopenPayRun,
  type Payslip,
} from "@/lib/payroll/api";

/**
 * Phase SC-2c — pay-run detail drawer.
 *
 * Shows a run's payslips and its gross / deduction / net totals, plus the
 * lifecycle actions gated on `manage_payroll`:
 *   - DRAFT     → add / edit / delete payslips, Finalize.
 *   - FINALIZED → read-only payslips, Reopen. (A finalized run's gross is
 *                 the month's staff-cost in the P&L, so finalize is the
 *                 "lock the number" moment.)
 * Delete (soft) is allowed in either state, behind a confirm.
 */

type PayslipDialog = { kind: "none" } | { kind: "create" } | { kind: "edit"; payslip: Payslip };
type ConfirmKind = "none" | "finalize" | "reopen" | "delete-run" | "delete-slip";

export interface PayRunDetailSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  runId: string | null;
  canManage: boolean;
  /** Called after the run is deleted so the parent can drop its selection. */
  onDeleted?: () => void;
}

export function PayRunDetailSheet({
  open,
  onOpenChange,
  runId,
  canManage,
  onDeleted,
}: PayRunDetailSheetProps) {
  const { data, isLoading, isError, refetch } = usePayRun(open ? runId : null);
  const { ct } = useTerms();
  const [payslipDialog, setPayslipDialog] = React.useState<PayslipDialog>({ kind: "none" });
  const [confirm, setConfirm] = React.useState<ConfirmKind>("none");
  const [slipToDelete, setSlipToDelete] = React.useState<Payslip | null>(null);

  const finalizeMutation = useFinalizePayRun();
  const reopenMutation = useReopenPayRun();
  const deleteRunMutation = useDeletePayRun();
  const deleteSlipMutation = useDeletePayslip();

  const run = data?.run ?? null;
  const payslips = data?.payslips ?? [];
  const isDraft = run?.status === "draft";
  const isFinalized = run?.status === "finalized";

  const closeConfirm = () => setConfirm("none");

  const handleFinalize = async () => {
    if (!runId) return;
    try {
      await finalizeMutation.mutateAsync(runId);
      toast.success(ct("Pay run finalized — the total is now this month's staff cost."));
      closeConfirm();
    } catch (err) {
      toast.error(errMessage(err, "Couldn't finalize the run."));
    }
  };

  const handleReopen = async () => {
    if (!runId) return;
    try {
      await reopenMutation.mutateAsync(runId);
      toast.success("Pay run reopened for edits.");
      closeConfirm();
    } catch (err) {
      toast.error(errMessage(err, "Couldn't reopen the run."));
    }
  };

  const handleDeleteRun = async () => {
    if (!runId) return;
    try {
      await deleteRunMutation.mutateAsync(runId);
      toast.success("Pay run deleted.");
      closeConfirm();
      onDeleted?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(errMessage(err, "Couldn't delete the run."));
    }
  };

  const handleDeleteSlip = async () => {
    if (!runId || !slipToDelete) return;
    try {
      await deleteSlipMutation.mutateAsync({ runId, payslipId: slipToDelete.id });
      toast.success(`Removed ${slipToDelete.staff_name} from this run.`);
      setSlipToDelete(null);
      closeConfirm();
    } catch (err) {
      toast.error(errMessage(err, "Couldn't remove the payslip."));
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          className="flex w-full flex-col gap-0 p-0 sm:max-w-[640px]"
          data-testid="pay-run-detail-sheet"
        >
          <SheetHeader className="border-border/60 border-b px-6 pb-4 pt-6">
            <div className="flex items-start gap-3">
              <span
                aria-hidden="true"
                className="bg-primary/10 text-primary mt-0.5 grid size-10 shrink-0 place-items-center rounded-lg"
              >
                <Wallet className="size-5" />
              </span>
              <div className="flex-1">
                <SheetTitle className="flex items-center gap-2 tracking-tight">
                  {run ? payPeriodLabel(run.year, run.month) : "Pay run"}
                  {run ? (
                    <StatusPill
                      status={isFinalized ? "active" : "invited"}
                      label={isFinalized ? "Finalized" : "Draft"}
                    />
                  ) : null}
                </SheetTitle>
                <SheetDescription className="mt-1 text-xs">
                  {isFinalized
                    ? ct("Locked — this month's staff cost in your profit & loss.")
                    : "Draft — edit payslips, then finalize to lock the total."}
                </SheetDescription>
              </div>
            </div>
          </SheetHeader>

          <div className="flex min-h-0 flex-1 flex-col">
            <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
              {isLoading ? (
                <div className="space-y-3">
                  <div className="bg-muted h-20 animate-pulse rounded-xl" />
                  <div className="bg-muted h-40 animate-pulse rounded-xl" />
                </div>
              ) : isError || !run ? (
                <div
                  role="alert"
                  className="border-destructive/30 bg-destructive/5 text-destructive flex items-start gap-2 rounded-md border px-3 py-2 text-sm"
                >
                  <AlertCircle aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
                  <span className="flex-1">Couldn&apos;t load this pay run.</span>
                  <Button variant="ghost" size="sm" onClick={() => refetch()}>
                    Retry
                  </Button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-3">
                    <TotalCard label="Gross" value={formatPaise(run.gross_total_paise)} />
                    <TotalCard
                      label="Deductions"
                      value={formatPaise(run.deduction_total_paise)}
                      muted
                    />
                    <TotalCard
                      label="Net payout"
                      value={formatPaise(run.net_total_paise)}
                      emphasis
                    />
                  </div>

                  {run.note ? (
                    <p className="text-muted-foreground border-border/60 bg-muted/20 rounded-md border px-3 py-2 text-xs">
                      {run.note}
                    </p>
                  ) : null}

                  <div>
                    <div className="mb-2 flex items-center justify-between">
                      <h3 className="text-sm font-medium">
                        Payslips{" "}
                        <span className="text-muted-foreground tabular-nums">
                          ({run.payslip_count})
                        </span>
                      </h3>
                      {canManage && isDraft ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setPayslipDialog({ kind: "create" })}
                          data-testid="payslip-add"
                        >
                          <Plus aria-hidden="true" className="size-4" />
                          Add payslip
                        </Button>
                      ) : null}
                    </div>

                    <div className="border-border overflow-hidden rounded-lg border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[34%]">Name</TableHead>
                            <TableHead className="w-[18%]">Role</TableHead>
                            <TableHead className="w-[16%] text-right">Gross</TableHead>
                            <TableHead className="w-[16%] text-right">Net</TableHead>
                            {canManage && isDraft ? (
                              <TableHead className="w-[8%] text-right">Action</TableHead>
                            ) : null}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {payslips.length === 0 ? (
                            <TableRow>
                              <TableCell
                                colSpan={canManage && isDraft ? 5 : 4}
                                className="text-muted-foreground py-8 text-center text-sm"
                              >
                                No payslips on this run.
                                {canManage && isDraft
                                  ? " Add team members to pay them this month."
                                  : ""}
                              </TableCell>
                            </TableRow>
                          ) : (
                            payslips.map((slip) => (
                              <TableRow key={slip.id} data-testid={`payslip-row-${slip.id}`}>
                                <TableCell className="font-medium">
                                  <div className="flex items-center gap-2">
                                    <span>{slip.staff_name}</span>
                                    <Badge variant="outline" className="text-[10px]">
                                      {slip.has_portal_access ? "Portal" : "Non-portal"}
                                    </Badge>
                                  </div>
                                  {slip.deduction_paise > 0 ? (
                                    <div className="text-muted-foreground text-xs tabular-nums">
                                      − {formatPaise(slip.deduction_paise)} deductions
                                    </div>
                                  ) : null}
                                </TableCell>
                                <TableCell className="text-muted-foreground text-sm">
                                  {slip.role_label}
                                </TableCell>
                                <TableCell className="text-right tabular-nums">
                                  {formatPaise(slip.gross_paise)}
                                </TableCell>
                                <TableCell className="text-right font-medium tabular-nums">
                                  {formatPaise(slip.net_paise)}
                                </TableCell>
                                {canManage && isDraft ? (
                                  <TableCell className="text-right">
                                    <DropdownMenu>
                                      <DropdownMenuTrigger asChild>
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          aria-label={`Actions for ${slip.staff_name}`}
                                          className="size-7"
                                        >
                                          <MoreHorizontal aria-hidden="true" className="size-4" />
                                        </Button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent align="end" className="w-40">
                                        <DropdownMenuItem
                                          onSelect={() =>
                                            setPayslipDialog({ kind: "edit", payslip: slip })
                                          }
                                        >
                                          <Pencil aria-hidden="true" className="size-4" />
                                          Edit
                                        </DropdownMenuItem>
                                        <DropdownMenuItem
                                          className="text-destructive focus:text-destructive"
                                          onSelect={() => {
                                            setSlipToDelete(slip);
                                            setConfirm("delete-slip");
                                          }}
                                        >
                                          <Trash2 aria-hidden="true" className="size-4" />
                                          Remove
                                        </DropdownMenuItem>
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                  </TableCell>
                                ) : null}
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                </>
              )}
            </div>

            {canManage && run ? (
              <div className="border-border/60 bg-card/40 flex items-center justify-between gap-2 border-t px-6 py-4 backdrop-blur">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={() => setConfirm("delete-run")}
                >
                  <Trash2 aria-hidden="true" className="size-4" />
                  Delete run
                </Button>
                {isDraft ? (
                  <Button
                    size="sm"
                    onClick={() => setConfirm("finalize")}
                    data-testid="run-finalize"
                  >
                    <Lock aria-hidden="true" className="size-4" />
                    Finalize run
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setConfirm("reopen")}
                    data-testid="run-reopen"
                  >
                    <RotateCcw aria-hidden="true" className="size-4" />
                    Reopen
                  </Button>
                )}
              </div>
            ) : null}
          </div>
        </SheetContent>
      </Sheet>

      {canManage && runId ? (
        <PayslipEditorSheet
          open={payslipDialog.kind !== "none"}
          onOpenChange={(next) => {
            if (!next) setPayslipDialog({ kind: "none" });
          }}
          runId={runId}
          existing={payslipDialog.kind === "edit" ? payslipDialog.payslip : null}
          onSaved={() => setPayslipDialog({ kind: "none" })}
        />
      ) : null}

      <ConfirmDialog
        open={confirm === "finalize"}
        onOpenChange={(v) => (v ? setConfirm("finalize") : closeConfirm())}
        title="Finalize this pay run?"
        description={ct(
          "Finalizing locks the payslips and makes the gross total this month's staff cost in your profit & loss. You can reopen it later if you need to change something.",
        )}
        confirmLabel="Finalize"
        icon={<Lock className="size-5" />}
        busy={finalizeMutation.isPending}
        onConfirm={handleFinalize}
      />
      <ConfirmDialog
        open={confirm === "reopen"}
        onOpenChange={(v) => (v ? setConfirm("reopen") : closeConfirm())}
        title="Reopen this pay run?"
        description={ct(
          "Reopening turns it back into a draft so you can edit payslips. Until you finalize again, the P&L uses your recurring staff cost estimate for this month.",
        )}
        confirmLabel="Reopen"
        icon={<RotateCcw className="size-5" />}
        busy={reopenMutation.isPending}
        onConfirm={handleReopen}
      />
      <ConfirmDialog
        open={confirm === "delete-run"}
        onOpenChange={(v) => (v ? setConfirm("delete-run") : closeConfirm())}
        title="Delete this pay run?"
        description="This removes the run and all its payslips. This can't be undone."
        confirmLabel="Delete"
        destructive
        icon={<Trash2 className="size-5" />}
        busy={deleteRunMutation.isPending}
        onConfirm={handleDeleteRun}
      />
      <ConfirmDialog
        open={confirm === "delete-slip"}
        onOpenChange={(v) => (v ? setConfirm("delete-slip") : closeConfirm())}
        title={`Remove ${slipToDelete?.staff_name ?? "this payslip"}?`}
        description="This payslip will be removed from the run and the totals recalculated."
        confirmLabel="Remove"
        destructive
        icon={<Trash2 className="size-5" />}
        busy={deleteSlipMutation.isPending}
        onConfirm={handleDeleteSlip}
      />
    </>
  );
}

function TotalCard({
  label,
  value,
  muted,
  emphasis,
}: {
  label: string;
  value: string;
  muted?: boolean;
  emphasis?: boolean;
}) {
  return (
    <div
      className={cn(
        "border-border/60 rounded-lg border px-3 py-2.5",
        emphasis ? "bg-primary/5 border-primary/30" : "bg-card",
      )}
    >
      <div className="text-muted-foreground text-xs">{label}</div>
      <div
        className={cn(
          "mt-0.5 tabular-nums",
          emphasis ? "text-base font-semibold" : "text-sm font-medium",
          muted && "text-muted-foreground",
        )}
      >
        {value}
      </div>
    </div>
  );
}

function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  destructive,
  icon,
  busy,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel: string;
  destructive?: boolean;
  icon: React.ReactNode;
  busy: boolean;
  onConfirm: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <span
            aria-hidden="true"
            className={cn(
              "mb-1 grid size-10 place-items-center rounded-lg",
              destructive ? "bg-destructive/10 text-destructive" : "bg-primary/10 text-primary",
            )}
          >
            {icon}
          </span>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="ghost" disabled={busy}>
              Cancel
            </Button>
          </DialogClose>
          <Button
            variant={destructive ? "destructive" : "default"}
            disabled={busy}
            onClick={onConfirm}
          >
            {busy ? (
              <Loader2 aria-hidden="true" className="size-4 animate-spin" />
            ) : destructive ? null : (
              <CheckCircle2 aria-hidden="true" className="size-4" />
            )}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function errMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.message || fallback;
  if (err instanceof Error) return err.message || fallback;
  return fallback;
}
